import React, { useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router';
import styled from 'styled-components';
import { Button, colors, Heading, Link, Paragraph, Spinner } from '@telia/styleguide';
import { useAuthenticate } from '../../util/SalesApi';

import SalesPackageView from '../../modules/sales-package/SalesPackageView';
import SalesPackageTV from '../../modules/sales-package/SalesPackageTV';
import { PackagesActions, SetPackageAction, AddressId } from '../../store/actions';
import { useStore } from '../../store/store';
import { SalesPackage } from '../../types';
import {
  pushAddToCart,
  pushPageview,
  pushProductClick,
  pushProductImpression,
  pushPackageFound,
  pushNoPackageFound
} from '../../util/GoogleTagManager';
import { RequestStatus } from '../../util/RequestStatus';
import { fetchSalesPackagesForValidAddressId } from '../../util/SalesApi';
import {
  routes,
  getPackageGroups,
  vaidAsQParam,
  backUrlAsParam,
  fallbackUrlAsParam,
  idWithTV,
  idWithoutTV,
  getSalesPackageById,
  AppleTvCPID
} from '../../util/utils';
import { MediaQueries } from '../../util/MediaQueries';

const SalesPackagePage: React.FC<RouteComponentProps> = props => {
  const { state, dispatch } = useStore();
  const { packages, address, auth } = state;
  useAuthenticate();
  const [tvChecked, setTvChecked] = useState(false);
  const addressId = address?.selectedAddress?.id;
  const noPackages = !packages.packages.packages?.length;

  useEffect(() => {
    let first = true;
    if (first && packages.requestStatus === RequestStatus.SUCCESS && !noPackages) {
      const packagesNames = packages.packages.packages?.map(pck => pck.name).join(' ');
      pushPackageFound(packagesNames);
      pushProductImpression(packages.packages.packages);
      first = false;
    }
  }, [packages.requestStatus]);

  useEffect(() => {
    if (!address.selectedAddress && !vaidAsQParam) {
      pushPageview(routes.address);
      props.history.push(routes.address);
    } else {
      if (addressId || vaidAsQParam) {
        if (auth.token) {
          fetchSalesPackagesForValidAddressId(
            addressId || (Number.parseInt(vaidAsQParam) as AddressId),
            dispatch,
            auth.token
          );
        }
      } else {
        console.warn('No id for selected address');
      }
    }
  }, [addressId, vaidAsQParam, auth.token]);

  const {
    fourGPackages,
    fiveGPackages,
    fiveGReadyPackages,
    isCabinProduct,
    hasCabinEstablishmentOffer,
    hasFreeInstallamentCabin,
    hasFiveGCabinInstamentOffer,
    hasInstallamentOfferFiveG,
    hasFreeInstallamentFourG,
    fiveGWithTvPackages,
    fiveGReadyWithTVPackages,
    fiveGReadyCabin,
    fiveGCabin
  } = getPackageGroups(packages.packages.packages);

  const hasInstallmentOffer =
    hasFreeInstallamentCabin || hasInstallamentOfferFiveG || hasFreeInstallamentFourG || hasFiveGCabinInstamentOffer;

  const canGetTv = fiveGReadyWithTVPackages.length > 0 || fiveGWithTvPackages.length > 0;

  const handleTvCheckChange = () => {
    setTvChecked(!tvChecked);
  };

  useEffect(() => {
    const selectedPackage = packages.selectedPackage;
    if (fiveGWithTvPackages.length > 0 || fiveGReadyWithTVPackages.length > 0) {
      if (selectedPackage !== undefined && tvChecked) {
        // set equivalent salespakge with tv

        const salesPackage = getSalesPackageById(
          [...fiveGWithTvPackages, ...fiveGReadyWithTVPackages],
          Number.parseInt(idWithTV[selectedPackage.id])
        );
        dispatch({ type: PackagesActions.SELECT_PACKAGE, payload: salesPackage } as SetPackageAction);
      }
      if (selectedPackage !== undefined && !tvChecked) {
        // set equivalent salespakge without tv
        const salesPackage = getSalesPackageById(
          [...fiveGPackages, ...fiveGReadyPackages],
          Number.parseInt(idWithoutTV[selectedPackage.id])
        );
        dispatch({ type: PackagesActions.SELECT_PACKAGE, payload: salesPackage } as SetPackageAction);
      }
    }
  }, [tvChecked]);

  const goToNextPage = () => {
    const selectedPackage = packages.selectedPackage;
    if (selectedPackage) {
      pushAddToCart([selectedPackage]);
    }
    if (
      selectedPackage!.additionalProducts.length > 0 &&
      selectedPackage!.additionalProducts[0].commercialProductId == AppleTvCPID
    ) {
      pushPageview(routes.additionalProducts);
      props.history.push(routes.additionalProducts);
    } else {
      pushPageview(routes.personalia);
      props.history.push(routes.personalia);
    }
  };

  if (packages.requestStatus === RequestStatus.PENDING || !packages.packages) {
    return (
      <Wrapper>
        <Spinner />
      </Wrapper>
    );
  }

  if (packages.requestStatus === RequestStatus.ERROR) {
    if (!!fallbackUrlAsParam) {
      window.location.href = window.location.origin + fallbackUrlAsParam;
    } else {
      return (
        <Wrapper>
          <HeadingContainer>
            <Heading tag={'h1'} text={'Noe gikk galt!'} size={'l'} />
          </HeadingContainer>
          <ErrorMessage>
            Noe gikk dessverre galt da vi søkte etter tilgjengelige pakker på din adresse. Ring oss gjerne på telefon
            05050, så finner vi beste løsning for deg.
          </ErrorMessage>
        </Wrapper>
      );
    }
  }

  if (packages.requestStatus === RequestStatus.SUCCESS && noPackages) {
    pushNoPackageFound();
    if (!!fallbackUrlAsParam) {
      window.location.href = window.location.origin + fallbackUrlAsParam;
    } else {
      return (
        <Wrapper>
          <HeadingContainer>
            <Heading tag={'h1'} size={'m'} text={'Ingen treff, men du kan fortsatt få internett'} />
          </HeadingContainer>
          <ErrorMessage>
            Vi kan dessverre ikke levere Trådløst Bredbånd på din adresse, men vi får deg på nett uansett. Velg gjerne
            et av følgende alternativer:
            <ul>
              <li>
                Ønsker du internett hjemme?
                <Link href="https://www.telia.no/internett/bredband/#skjema"> Ta kontakt med oss</Link>, så finner vi
                beste løsning
              </li>
              for deg.
              <li>
                Ønsker du internett på hytta? Da er mobilt bredbånd et godt alternativ
                <Link href="https://www.telia.no/internett/mobilt-bredband/">les mer og bestill her</Link>.
              </li>
            </ul>
          </ErrorMessage>
          <Link
            href={''}
            onClick={(e: React.MouseEvent<HTMLElement>) => {
              e.preventDefault();
              props.history.goBack();
            }}
            text="Tilbake"
            icon={'arrow-left'}
            iconPosition={'before'}
          />
        </Wrapper>
      );
    }
  }

  const disableNext = !packages.selectedPackage;

  const onPackageSelect = (salesPackage: SalesPackage, tvChecked: boolean) => {
    if (fiveGWithTvPackages.length > 0 || fiveGReadyWithTVPackages.length > 0) {
      if (salesPackage !== undefined && tvChecked) {
        // set equivalent salespakge with tv
        const selectedSalesPackage = getSalesPackageById(
          [...fiveGWithTvPackages, ...fiveGReadyWithTVPackages],
          Number.parseInt(idWithTV[salesPackage.id + ''])
        );
        dispatch({ type: PackagesActions.SELECT_PACKAGE, payload: selectedSalesPackage } as SetPackageAction);
      }
      if (salesPackage !== undefined && !tvChecked) {
        // set equivalent salespakge without tv
        const selectedSalesPackage = getSalesPackageById([...fiveGPackages, ...fiveGReadyPackages], salesPackage.id);
        dispatch({ type: PackagesActions.SELECT_PACKAGE, payload: selectedSalesPackage } as SetPackageAction);
      }
    } else {
      dispatch({ type: PackagesActions.SELECT_PACKAGE, payload: salesPackage } as SetPackageAction);
    }
  };
  const isSelected = (selectedPackage: SalesPackage, salesPackage: SalesPackage, tvChecked: boolean): boolean => {
    if (tvChecked) {
      if (selectedPackage.id === Number.parseInt(idWithTV[salesPackage.id + ''])) {
        return true;
      }
    }
    return selectedPackage.id === salesPackage.id;
  };

  return (
    <Container>
      <Wrapper>
        <PackageContainer>
          {fiveGPackages.length > 0 && (
            <Packages>
              <StyledHeading tag="h2" size="l" text={'Du kan få Trådløst Bredbånd'} />
              {fiveGPackages.map((salesPackage: SalesPackage, index) => {
                return (
                  <SalesPackageView
                    key={index}
                    hasInstallmentOffer={hasInstallmentOffer}
                    salesPackage={salesPackage}
                    onSelect={() => {
                      onPackageSelect(salesPackage, tvChecked);
                      pushProductClick(salesPackage, index);
                    }}
                    selected={
                      !!packages.selectedPackage &&
                      !!salesPackage &&
                      !!isSelected(packages.selectedPackage, salesPackage, tvChecked)
                    }
                  />
                );
              })}
              <Paragraph>
                På denne adressen tilbyr vi Trådløst Bredbånd. Det betyr at du får raskt og stabilt internett via
                5G-mobilnettet
              </Paragraph>
              <Paragraph>
                Installasjonskostnad er på <strong>{hasInstallamentOfferFiveG ? 999 : 3499},-</strong> og inkluderer
                montering. {canGetTv ? 'Kostnadsfri ved kjøp av TV over Trådløst Bredbånd' : ''} <br /> Tilbudet
                forutsetter en bindingstid på 12 md.
              </Paragraph>
            </Packages>
          )}
          {fiveGPackages.length === 0 && fiveGReadyPackages.length > 0 && (
            <Packages>
              <StyledHeading tag="h2" size="l" text={'Du kan få Trådløst Bredbånd'} />
              {fiveGReadyPackages.map((salesPackage: SalesPackage, index) => {
                return (
                  <SalesPackageView
                    key={index}
                    hasInstallmentOffer={hasInstallmentOffer}
                    salesPackage={salesPackage}
                    onSelect={() => {
                      onPackageSelect(salesPackage, tvChecked);
                      pushProductClick(salesPackage, index);
                    }}
                    selected={
                      packages.selectedPackage !== undefined &&
                      isSelected(packages.selectedPackage, salesPackage, tvChecked)
                    }
                  />
                );
              })}
              <Paragraph>
                På denne adressen tilbyr vi Trådløst Bredbånd. Det betyr at du får raskt og stabilt internett via
                4G-mobilnettet
              </Paragraph>
              <Paragraph>
                <span>
                  Installasjonskostnad er på <strong> {hasFreeInstallamentCabin ? 0 : '999'},-</strong> og inkluderer
                  montering. {canGetTv ? 'Kostnadsfri ved kjøp av TV over Trådløst Bredbånd' : ''}
                  <br /> Tilbudet forutsetter en bindingstid på 12 md.
                </span>
              </Paragraph>
            </Packages>
          )}
          {(fiveGWithTvPackages.length > 0 || (fiveGReadyWithTVPackages.length > 0 && fiveGPackages.length === 0)) && (
            <Packages
              style={{ visibility: packages.selectedPackage && packages.selectedPackage.id ? 'visible' : 'hidden' }}
            >
              <StyledHeading tag="h2" size="l" text={'TV over Trådløst Bredbånd'} />
              <SalesPackageTV
                salesPackage={packages.selectedPackage}
                handleTvCheckChange={() => {
                  handleTvCheckChange();
                }}
                tvChecked={tvChecked}
              />
            </Packages>
          )}
          {fiveGPackages.length === 0 && fiveGReadyPackages.length === 0 && fourGPackages.length > 0 && (
            <Packages>
              <StyledHeading tag="h2" size="l" text={'Du kan få Trådløst Bredbånd 4G'} />
              {fourGPackages.map((salesPackage: SalesPackage, index) => {
                return (
                  <SalesPackageView
                    key={index}
                    hasInstallmentOffer={hasInstallmentOffer}
                    salesPackage={salesPackage}
                    onSelect={() => {
                      dispatch({ type: PackagesActions.SELECT_PACKAGE, payload: salesPackage } as SetPackageAction);
                      pushProductClick(salesPackage, index);
                    }}
                    selected={packages.selectedPackage !== undefined && packages.selectedPackage.id === salesPackage.id}
                  />
                );
              })}
              {isCabinProduct ? (
                hasCabinEstablishmentOffer ? (
                  <>
                    <Paragraph>
                      <CampaignText>Kampanje!</CampaignText> Installasjon kr 699,- (normalpris kr 1 999,-).
                    </Paragraph>
                    <Paragraph>
                      På denne adressen tilbyr vi Trådløst Bredbånd. Det betyr at du får raskt og stabilt internett via
                      4G-mobilnettet
                    </Paragraph>
                    <Paragraph>
                      Abonnementene har 12 md. bindingstid, og inkluderer en utendørs antenneboks, innendørs Wifi 6
                      router og Trygg på nett-pakken.
                    </Paragraph>
                  </>
                ) : (
                  <div>
                    Installasjonskostnad er på <strong> {hasFreeInstallamentCabin ? 0 : '999'},-</strong> og inkluderer
                    montering.
                    <br /> Tilbudet forutsetter en bindingstid på 12 md.
                  </div>
                )
              ) : (
                <>
                  <Paragraph>
                    På denne adressen tilbyr vi Trådløst Bredbånd. Det betyr at du får raskt og stabilt internett via
                    4G-mobilnettet
                  </Paragraph>
                  <Paragraph>
                    Installasjonskostnad er på <strong> {hasFreeInstallamentFourG ? 0 : 699},-</strong> og inkluderer
                    montering. {canGetTv ? 'Kostnadsfri ved kjøp av TV over Trådløst Bredbånd' : ''}
                    <br /> Tilbudet forutsetter en bindingstid på 12 md.
                  </Paragraph>
                </>
              )}
            </Packages>
          )}
          {fiveGPackages.length === 0 && fiveGReadyPackages.length === 0 && fiveGReadyCabin.length > 0 && (
            <Packages>
              <StyledHeading tag="h2" size="l" text={'Du kan få Trådløst Bredbånd'} />
              {fiveGReadyCabin.map((salesPackage: SalesPackage, index) => {
                return (
                  <SalesPackageView
                    key={index}
                    hasInstallmentOffer={hasFiveGCabinInstamentOffer}
                    salesPackage={salesPackage}
                    onSelect={() => {
                      dispatch({ type: PackagesActions.SELECT_PACKAGE, payload: salesPackage } as SetPackageAction);
                      pushProductClick(salesPackage, index);
                    }}
                    selected={packages.selectedPackage !== undefined && packages.selectedPackage.id === salesPackage.id}
                  />
                );
              })}
              <>
                {hasFiveGCabinInstamentOffer && (
                  <Paragraph>
                    <CampaignText>Kampanje!</CampaignText> Installasjon kr 999,- (normalpris kr 1 999,-).
                  </Paragraph>
                )}
                <Paragraph>
                  På denne adressen tilbyr vi Trådløst Bredbånd. Det betyr at du får raskt og stabilt internett via
                  4G-mobilnettet
                </Paragraph>
                <Paragraph>
                  Abonnementene har 12 md. bindingstid, og inkluderer en utendørs antenneboks, innendørs Wifi 6 router
                  og Trygg på nett-pakken.
                </Paragraph>
              </>
            </Packages>
          )}
          {fiveGPackages.length === 0 && fiveGReadyPackages.length === 0 && fiveGCabin.length > 0 && (
            <Packages>
              <StyledHeading tag="h2" size="l" text={'Du kan få Trådløst Bredbånd'} />
              {fiveGCabin.map((salesPackage: SalesPackage, index) => {
                return (
                  <SalesPackageView
                    key={index}
                    hasInstallmentOffer={hasFiveGCabinInstamentOffer}
                    salesPackage={salesPackage}
                    onSelect={() => {
                      dispatch({ type: PackagesActions.SELECT_PACKAGE, payload: salesPackage } as SetPackageAction);
                      pushProductClick(salesPackage, index);
                    }}
                    selected={packages.selectedPackage !== undefined && packages.selectedPackage.id === salesPackage.id}
                  />
                );
              })}
              <>
                {hasFiveGCabinInstamentOffer && (
                  <Paragraph>
                    <CampaignText>Kampanje!</CampaignText> Installasjon kr 999,- (normalpris kr 1 999,-).
                  </Paragraph>
                )}
                <Paragraph>
                  På denne adressen tilbyr vi Trådløst Bredbånd. Det betyr at du får raskt og stabilt internett via
                  5G-mobilnettet
                </Paragraph>
                <Paragraph>
                  Abonnementene har 12 md. bindingstid, og inkluderer en utendørs antenneboks, innendørs Wifi 6 router
                  og Trygg på nett-pakken.
                </Paragraph>
              </>
            </Packages>
          )}
        </PackageContainer>
      </Wrapper>
      <ButtonContainer>
        <Link
          href={''}
          onClick={(e: React.MouseEvent<HTMLElement>) => {
            e.preventDefault();
            if (!!backUrlAsParam) {
              window.location.href = window.location.origin + backUrlAsParam;
            } else {
              props.history.goBack();
            }
          }}
          text="Tilbake"
          icon={'arrow-left'}
          iconPosition={'before'}
        />
        <HorizontalSpacer />
        <Button onClick={goToNextPage} kind={'primary'} isDisabled={disableNext} text="Fortsett" />
      </ButtonContainer>
    </Container>
  );
};

export default SalesPackagePage;
const Container = styled.div`
  margin: 0 auto 1rem;
  padding: 0 3rem;
  text-align: left;
  max-width: 1350px;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  ${MediaQueries.onlyMobile} {
    padding: 0;
  }
`;
const CampaignText = styled.span`
  color: ${colors.corePurple};
  ${MediaQueries.onlyMobile} {
    display: block;
  }
`;
const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const PackageContainer = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  width: 100%;
  align-items: start;
`;
const Packages = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: flex-start;
  flex: 0 1 560px;
  margin: 2rem;
  ${MediaQueries.onlyMobile} {
    margin: 2rem 0;
  }
`;

const StyledHeading = styled(Heading)`
  && {
    color: ${colors.corePurple};
    font-family: Pebble, Helvetica, Roboto, Arial, sans-serif;
    align-self: flex-start;
  }
`;

const ButtonContainer = styled.div`
  margin-top: 2rem;
  display: flex;
  align-items: center;
  align-self: flex-end;
  ${MediaQueries.onlyMobile} {
    margin-right: 1rem;
  }
`;

const HorizontalSpacer = styled.div`
  margin: 0 1rem;
`;

const HeadingContainer = styled.div`
  display: flex;
  align-items: center;
`;

const ErrorMessage = styled.p`
  margin-top: 2rem;
  text-align: left;
  font-weight: 300;
  max-width: 40rem;
`;
